import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RecaptchaVerifier, signInWithPhoneNumber, signInWithPopup } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { LuCarFront, LuCircleAlert, LuCircleCheck, LuLogIn, LuPhone, LuUserPlus, LuUserRound } from "react-icons/lu";
import { appleProvider, auth, authPersistenceReady, db, functions, googleProvider } from "../firebase";
import {
  confirmNativePhoneCode,
  getAuthErrorMessage,
  isNativeAndroidAuth,
  isNativeIosAuth,
  logAuthCheckpoint,
  logAuthError,
  signInWithNativeApple,
  signInWithNativeGoogle,
  startNativePhoneVerification,
} from "../services/nativeAuthService";
import logoZuno from "../assets/zuno-logo.png";
import "./Login.css";
import { bindClientStorageToUser } from "../services/clientStorageSecurity";
import { isPassengerProfileCompleteForUser } from "../services/passengerProfileService";

let recaptchaVerifier = null;

export const AUTH_COPY = {
  login: {
    helper: "Digite seu número de telefone para continuar.",
  },
};

export default function Login() {
  const [mode, setMode] = useState("login");
  const [userType, setUserType] = useState("passenger");
  const [telefone, setTelefone] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loadingSMS, setLoadingSMS] = useState(false);
  const [smsLoadingLabel, setSmsLoadingLabel] = useState("Enviando código...");
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [loadingApple, setLoadingApple] = useState(false);
  const [etapa, setEtapa] = useState("telefone");
  const [codigoSMS, setCodigoSMS] = useState("");
  const [confirmacaoSMS, setConfirmacaoSMS] = useState(null);
  const [nativeVerificationId, setNativeVerificationId] = useState("");
  const [contador, setContador] = useState(0);
  const contadorTimerRef = useRef(null);
  const lastAutoSubmittedCodeRef = useRef("");
  const loginRootRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get("returnTo") === "/excluir-conta"
    ? "/excluir-conta"
    : "";

  useEffect(() => {
    const resetLoginScroll = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (loginRootRef.current) {
        loginRootRef.current.scrollTop = 0;
        loginRootRef.current.scrollLeft = 0;
      }
    };
    resetLoginScroll();
    const animationFrame = requestAnimationFrame(resetLoginScroll);
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  useEffect(() => {
    const root = loginRootRef.current;
    if (
      !root ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      window.matchMedia("(pointer: coarse)").matches
    ) return undefined;

    let animationFrame = 0;
    const setParallax = (clientX, clientY) => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        const x = (clientX / window.innerWidth - 0.5) * 2;
        const y = (clientY / window.innerHeight - 0.5) * 2;
        root.style.setProperty("--scene-x", `${x * -5}px`);
        root.style.setProperty("--scene-y", `${y * -4}px`);
        root.style.setProperty("--depth-x", `${x * 11}px`);
        root.style.setProperty("--depth-y", `${y * 8}px`);
        root.style.setProperty("--depth-x-reverse", `${x * -11}px`);
        root.style.setProperty("--depth-y-reverse", `${y * -8}px`);
        root.style.setProperty("--particle-x", `${x * 17}px`);
        root.style.setProperty("--particle-y", `${y * 13}px`);
      });
    };
    const handlePointerMove = (event) => setParallax(event.clientX, event.clientY);
    const resetParallax = () => setParallax(window.innerWidth / 2, window.innerHeight / 2);

    window.addEventListener("pointermove", handlePointerMove, { passive: true });
    window.addEventListener("pointerleave", resetParallax);
    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerleave", resetParallax);
    };
  }, []);

  const verificarPerfilPassageiro = async (user) => {
    try {
      const snap = await getDoc(doc(db, "users", user.uid));
      return snap.exists() && isPassengerProfileCompleteForUser(user, snap.data());
    } catch {
      return false;
    }
  };

  const redirecionarUsuario = useCallback(async (user, tipo, viaSMS = false, provider = "unknown") => {
    if (!user) throw new Error("Firebase authentication completed without a user.");
    logAuthCheckpoint(`${provider}/auth-success`, { uidSuffix: user.uid?.slice(-6) || null });
    await user.getIdToken();
    logAuthCheckpoint(`${provider}/token-ready`, { hasToken: true });
    bindClientStorageToUser(user.uid);
    localStorage.setItem("usuarioLogado", user.displayName || user.email?.split("@")[0] || "");
    localStorage.setItem("usuarioFoto", user.photoURL || "");
    localStorage.setItem("userEmail", user.email || "");
    localStorage.setItem("userType", tipo);
    logAuthCheckpoint(`${provider}/application-session-created`, {
      hasCurrentUser: Boolean(auth.currentUser),
      currentUserMatches: auth.currentUser?.uid === user.uid,
      uidSuffix: user.uid?.slice(-6) || null,
    });
    if (returnTo) {
      logAuthCheckpoint(`${provider}/navigation`, { destination: returnTo });
      navigate(returnTo);
      return;
    }
    if (tipo === "driver") {
      logAuthCheckpoint(`${provider}/profile-load`, { profileType: "driver" });
      try {
        const [driverSnap, kycSnap, onboardingSnap] = await Promise.all([
          getDoc(doc(db, "drivers", user.uid)),
          getDoc(doc(db, "driver_kyc", user.uid)),
          getDoc(doc(db, "driver_onboarding", user.uid)),
        ]);
        if (driverSnap.exists()) {
          const isFullyApproved = driverSnap.data().status === "approved"
            && kycSnap.exists() && kycSnap.data().status === "APPROVED"
            && onboardingSnap.exists() && onboardingSnap.data().status === "COMPLETED";
          if (isFullyApproved) {
            logAuthCheckpoint(`${provider}/navigation`, { destination: "driver-home" });
            logAuthCheckpoint("NAVIGATION_HOME", { provider, destination: "driver-home" });
            navigate("/driver-home");
          } else if (driverSnap.data().status === "pending" && onboardingSnap.data()?.status === "SUBMITTED") {
            logAuthCheckpoint(`${provider}/navigation`, { destination: "driver-review" });
            navigate("/motorista-analise");
          } else navigate("/cadastro-motorista");
          return;
        }
        const motoristaSnap = await getDoc(doc(db, "motoristas", user.uid));
        logAuthCheckpoint(`${provider}/navigation`, { destination: motoristaSnap.exists() ? "driver-review" : "driver-register" });
        navigate(motoristaSnap.exists() ? "/motorista-analise" : "/cadastro-motorista");
      } catch (error) {
        logAuthError("driver-profile", error, { stage: `${provider}/profile-load`, provider, webAuth: auth });
        navigate("/cadastro-motorista");
      }
      return;
    }

    logAuthCheckpoint(`${provider}/profile-load`, { profileType: "passenger" });
    if (!(await verificarPerfilPassageiro(user))) {
      logAuthCheckpoint(`${provider}/navigation`, { destination: "complete-profile" });
      navigate("/completar-perfil");
      return;
    }
    logAuthCheckpoint(`${provider}/navigation`, { destination: "passenger-home" });
    logAuthCheckpoint("NAVIGATION_HOME", { provider, destination: "passenger-home" });
    navigate("/passenger-home");
  }, [navigate, returnTo]);

  useEffect(() => {
    return () => {
      if (contadorTimerRef.current) clearInterval(contadorTimerRef.current);
    };
  }, []);

  const handleGoogleLogin = async () => {
    if (loadingGoogle) return;
    let authStage = "google/terms";
    try {
      setLoadingGoogle(true);
      setErrorMessage("");
      authStage = "google/persistence";
      localStorage.setItem("userType", userType);
      authStage = "google/provider";
      const result = isNativeAndroidAuth() || isNativeIosAuth()
        ? await signInWithNativeGoogle(auth, functions, authPersistenceReady)
        : await signInWithPopup(auth, googleProvider);
      authStage = "google/post-auth";
      logAuthCheckpoint("APP_SESSION_CREATED", { provider: "google" });
      await redirecionarUsuario(result.user, userType, false, "google");
    } catch (error) {
      logAuthError("google-sign-in", error, { stage: authStage, provider: "google", webAuth: auth });
      setErrorMessage(getAuthErrorMessage(error, "google"));
    } finally {
      setLoadingGoogle(false);
    }
  };

  const handleAppleLogin = async () => {
    if (loadingApple) return;
    let authStage = "apple/persistence";
    try {
      setLoadingApple(true);
      setErrorMessage("");
      localStorage.setItem("userType", userType);
      authStage = "apple/provider";
      const result = isNativeIosAuth()
        ? await signInWithNativeApple(auth, functions, authPersistenceReady)
        : await signInWithPopup(auth, appleProvider);
      authStage = "apple/post-auth";
      logAuthCheckpoint("APP_SESSION_CREATED", { provider: "apple" });
      await redirecionarUsuario(result.user, userType, false, "apple");
    } catch (error) {
      logAuthError("apple-sign-in", error, { stage: authStage, provider: "apple", webAuth: auth });
      setErrorMessage(getAuthErrorMessage(error, "apple"));
    } finally {
      setLoadingApple(false);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    let numeroLimpo = telefone.replace(/\D/g, "");
    if (numeroLimpo.startsWith("55") && numeroLimpo.length > 11) numeroLimpo = numeroLimpo.slice(2);
    if (!numeroLimpo) {
      setErrorMessage("Por favor, informe seu telefone.");
      return;
    }
    if (numeroLimpo.length < 10 || numeroLimpo.length > 11) {
      setErrorMessage("Telefone inválido. Use DDD + número.");
      return;
    }

    let authStage = "phone/persistence";
    try {
      setLoadingSMS(true);
      const usesNativePhoneAuth = isNativeAndroidAuth() || isNativeIosAuth();
      setSmsLoadingLabel(usesNativePhoneAuth ? "Verificando segurança..." : "Enviando código...");
      localStorage.setItem("userType", userType);
      const phoneNumber = `+55${numeroLimpo}`;
      if (usesNativePhoneAuth) {
        authStage = "phone/native-verification";
        setNativeVerificationId(await startNativePhoneVerification(phoneNumber));
        setConfirmacaoSMS(null);
      } else {
        authStage = "phone/web-verification";
        if (!recaptchaVerifier) {
          recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
            size: "invisible",
            callback: () => {},
            "expired-callback": () => { recaptchaVerifier = null; },
          });
        }
        setConfirmacaoSMS(await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier));
        setNativeVerificationId("");
      }
      setSmsLoadingLabel("Enviando código...");
      setEtapa("codigo");
      setSuccessMessage(`Código enviado para ${telefone}`);
      setContador(60);
      contadorTimerRef.current = setInterval(() => {
        setContador((previous) => {
          if (previous <= 1) {
            clearInterval(contadorTimerRef.current);
            contadorTimerRef.current = null;
            return 0;
          }
          return previous - 1;
        });
      }, 1000);
    } catch (error) {
      recaptchaVerifier = null;
      logAuthError("phone-send-code", error, { stage: authStage, provider: "phone", webAuth: auth });
      setErrorMessage(getAuthErrorMessage(error, "sms"));
    } finally {
      setLoadingSMS(false);
      setSmsLoadingLabel("Enviando código...");
    }
  };

  const handleVerificarCodigo = async (event) => {
    event?.preventDefault?.();
    if (loadingSMS) return;
    setErrorMessage("");
    if (codigoSMS.length !== 6) {
      setErrorMessage("O código deve ter 6 dígitos.");
      return;
    }
    let authStage = "phone/code-confirm";
    try {
      setLoadingSMS(true);
      localStorage.setItem("userType", userType);
      const result = nativeVerificationId
        ? await confirmNativePhoneCode(auth, functions, nativeVerificationId, codigoSMS, authPersistenceReady)
        : await confirmacaoSMS.confirm(codigoSMS);
      authStage = "phone/post-auth";
      logAuthCheckpoint("APP_SESSION_CREATED", { provider: "phone" });
      localStorage.setItem("telefone", telefone.replace(/\D/g, ""));
      await redirecionarUsuario(result.user, userType, true, "phone");
    } catch (error) {
      logAuthError("phone-confirm-code", error, { stage: authStage, provider: "phone", webAuth: auth });
      setErrorMessage(getAuthErrorMessage(error, "code"));
      if (error.code === "auth/code-expired" || error.code === "auth/session-expired") setEtapa("telefone");
    } finally {
      setLoadingSMS(false);
    }
  };

  useEffect(() => {
    if (etapa !== "codigo" || codigoSMS.length !== 6) {
      if (codigoSMS.length < 6) lastAutoSubmittedCodeRef.current = "";
      return undefined;
    }
    if (loadingSMS || lastAutoSubmittedCodeRef.current === codigoSMS) return undefined;

    lastAutoSubmittedCodeRef.current = codigoSMS;
    const timeoutId = window.setTimeout(() => {
      handleVerificarCodigo();
    }, 150);
    return () => window.clearTimeout(timeoutId);
  }, [codigoSMS, etapa, loadingSMS]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTelefoneChange = (event) => {
    let value = event.target.value.replace(/\D/g, "");
    if (value.startsWith("55") && value.length > 11) value = value.slice(2);
    value = value.slice(0, 11);
    if (value.length > 6) value = value.replace(/^(\d{2})(\d{5})(\d+)/, "($1) $2-$3");
    else if (value.length > 2) value = value.replace(/^(\d{2})(\d+)/, "($1) $2");
    setTelefone(value);
    if (errorMessage) setErrorMessage("");
  };

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setErrorMessage("");
    setSuccessMessage("");
    setEtapa("telefone");
    setCodigoSMS("");
    setNativeVerificationId("");
  };

  const resetPhoneStep = () => {
    setEtapa("telefone");
    setCodigoSMS("");
    setNativeVerificationId("");
    setConfirmacaoSMS(null);
    setErrorMessage("");
    setSuccessMessage("");
    recaptchaVerifier = null;
  };

  return (
    <div ref={loginRootRef} className={`zuno-cosmic-login zuno-cosmic-login--${userType}`}>
      <div className="cosmic-login-scene" aria-hidden="true" />
      <div className="cosmic-login-shimmer" aria-hidden="true" />
      <svg className="cosmic-login-network" viewBox="0 0 1000 360" preserveAspectRatio="none" aria-hidden="true">
        {[
          { id: "one", from: [74, 235], to: [255, 112], path: "M74 235 C126 140 182 94 255 112" },
          { id: "two", from: [255, 112], to: [432, 182], path: "M255 112 C322 104 357 202 432 182" },
          { id: "three", from: [432, 182], to: [612, 105], path: "M432 182 C500 177 541 97 612 105" },
          { id: "four", from: [612, 105], to: [790, 196], path: "M612 105 C678 99 720 211 790 196" },
          { id: "five", from: [790, 196], to: [938, 82], path: "M790 196 C846 178 878 94 938 82" },
        ].map(({ id, from, to, path }) => (
          <g className={`cosmic-network-hop cosmic-network-hop--${id}`} key={id}>
            <path className="cosmic-network-route-base" d={path} />
            <path className="cosmic-network-route cosmic-network-route-trace" pathLength="1" d={path} />
            <path className="cosmic-network-route cosmic-network-route-signal" pathLength="1" d={path} />
            <circle className="cosmic-network-node cosmic-network-node--origin" cx={from[0]} cy={from[1]} r="4" />
            <circle className="cosmic-network-node cosmic-network-node--destination" cx={to[0]} cy={to[1]} r="4.5" />
            <circle className="cosmic-network-ripple" cx={to[0]} cy={to[1]} r="8" />
          </g>
        ))}
      </svg>
      <div className="cosmic-login-depth" aria-hidden="true">
        <div className="cosmic-planet-aura cosmic-planet-aura--top" />
      </div>
      <div className="cosmic-login-particles cosmic-login-particles--far" aria-hidden="true">
        {Array.from({ length: 10 }, (_, index) => <span key={`far-${index}`} />)}
      </div>
      <div className="cosmic-login-particles cosmic-login-particles--near" aria-hidden="true">
        {Array.from({ length: 8 }, (_, index) => <span key={`near-${index}`} />)}
      </div>
      <div id="recaptcha-container" className="cosmic-recaptcha" />

      <main className="cosmic-login-content">
        <header className="cosmic-login-brand">
          <img src={logoZuno} alt="Zuno" />
          <span>MOBILIDADE PARA TODOS</span>
        </header>

        <section className="cosmic-login-form" aria-label="Acesso à Zuno">
          <div className="cosmic-login-tabs" role="tablist" aria-label="Entrar ou criar conta">
            {["login", "cadastro"].map((item) => (
              <button key={item} type="button" role="tab" aria-selected={mode === item} data-active={mode === item} onClick={() => handleModeChange(item)}>
                {item === "login" ? <LuLogIn aria-hidden="true" /> : <LuUserPlus aria-hidden="true" />}
                {item === "login" ? "Entrar" : "Criar conta"}
              </button>
            ))}
          </div>

          <div className="cosmic-login-roles" aria-label="Tipo de usuário">
            <button type="button" data-active={userType === "passenger"} onClick={() => setUserType("passenger")}><LuUserRound aria-hidden="true" /> Passageiro</button>
            <button type="button" data-active={userType === "driver"} onClick={() => setUserType("driver")}><LuCarFront aria-hidden="true" /> Motorista</button>
          </div>

          {errorMessage && <div className="cosmic-login-alert cosmic-login-alert--error" role="alert"><LuCircleAlert /><span>{errorMessage}</span></div>}
          {successMessage && <div className="cosmic-login-alert cosmic-login-alert--success" role="status"><LuCircleCheck /><span>{successMessage}</span></div>}

          {etapa === "telefone" && (
            <div className="cosmic-login-panel">
              <p id="login-phone-helper" className="cosmic-sr-only">{AUTH_COPY.login.helper}</p>
              <label className="cosmic-phone-field">
                <LuPhone aria-hidden="true" />
                <strong>+55</strong>
                <span aria-hidden="true" />
                <input type="text" placeholder="Telefone com DDD" value={telefone} onChange={handleTelefoneChange} inputMode="numeric" autoComplete="tel" maxLength={15} aria-label="Telefone com DDD" aria-describedby="login-phone-helper" />
              </label>
              <button type="button" className="cosmic-login-action cosmic-login-action--sms" onClick={handleLogin} disabled={loadingSMS}>
                {loadingSMS ? <Spinner label={smsLoadingLabel} /> : <><span className="cosmic-message-icon" aria-hidden="true" /> Enviar código SMS</>}
              </button>
              <Divider />
              <GoogleButton loading={loadingGoogle} onClick={handleGoogleLogin} />
              <AppleButton loading={loadingApple} onClick={handleAppleLogin} />
            </div>
          )}

          {etapa === "codigo" && (
            <div className="cosmic-login-panel cosmic-code-panel">
              <div className="cosmic-code-sent"><small>Código enviado para</small><strong>{telefone}</strong></div>
              <input className="cosmic-code-input" type="text" placeholder="000000" value={codigoSMS} onChange={(event) => { setCodigoSMS(event.target.value.replace(/\D/g, "").slice(0, 6)); if (errorMessage) setErrorMessage(""); }} inputMode="numeric" autoComplete="one-time-code" maxLength={6} aria-label="Código SMS de seis dígitos" />
              <div className="cosmic-code-dots" aria-hidden="true">{[0, 1, 2, 3, 4, 5].map((index) => <span key={index} data-filled={index < codigoSMS.length} />)}</div>
              <button type="button" className="cosmic-login-action cosmic-login-action--verify" onClick={handleVerificarCodigo} disabled={loadingSMS || codigoSMS.length !== 6}>{loadingSMS ? <Spinner label="Verificando..." /> : <><LuCircleCheck /> Verificar código</>}</button>
              <div className="cosmic-code-actions">
                <button type="button" onClick={resetPhoneStep}>← Trocar número</button>
                <button type="button" disabled={contador > 0} onClick={() => { if (contador === 0) resetPhoneStep(); }}>{contador > 0 ? `Reenviar em ${contador}s` : "Reenviar SMS"}</button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function GoogleButton({ loading, onClick }) {
  return (
    <button type="button" className="cosmic-login-action cosmic-login-action--google" onClick={onClick} disabled={loading}>
      {loading ? <Spinner label="Entrando..." /> : <><span className="cosmic-google-mark" aria-hidden="true">G</span> Entrar com Google</>}
    </button>
  );
}

function AppleButton({ loading, onClick }) {
  return (
    <button type="button" className="cosmic-login-action cosmic-login-action--apple" onClick={onClick} disabled={loading}>
      {loading ? <Spinner label="Entrando..." /> : <><span className="cosmic-apple-mark" aria-hidden="true">&#63743;</span> Entrar com Apple</>}
    </button>
  );
}

function Spinner({ label }) {
  return <span className="cosmic-spinner"><span aria-hidden="true" />{label}</span>;
}

function Divider() {
  return <div className="cosmic-login-divider"><span /><small>ou</small><span /></div>;
}
